import React, { useState, useEffect, useCallback } from "react";
import ApiService from "../../_services/ApiService";
import AuthenticationService from "../../_services/AuthenticationService";
import { Modal } from "react-bootstrap";

import "./lcs-access-requests.css";

interface AccessRequest {
  Id: number;
  LessonPlanStepId: number;
  LessonPlanId: number;
  BatchId: number;
  RequestedByUserId: number;
  RequestedAt: string;
  Reason: string | null;
  Status: string;
  ReviewedByUserId: number | null;
  ReviewedAt: string | null;
  ReviewRemarks: string | null;
  UnlockExpiresAt: string | null;
  FacultyName: string | null;
  FacultyEmail: string | null;
  StepName: string | null;
  BatchName: string | null;
  PlannedEndDate: string | null;
  ReviewerName: string | null;
}

type StatusFilter = "ALL" | "PENDING" | "APPROVED" | "REJECTED";

const STATUS_TABS: { key: StatusFilter; label: string }[] = [
  { key: "ALL", label: "All" },
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "REJECTED", label: "Rejected" },
];

const LcsAccessRequests: React.FC = () => {
  const [requests, setRequests] = useState<AccessRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [statusFilter, setStatusFilter] = useState<StatusFilter>("PENDING");
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});

  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [reviewingRequest, setReviewingRequest] = useState<AccessRequest | null>(null);
  const [reviewAction, setReviewAction] = useState<"APPROVED" | "REJECTED" | null>(null);
  const [reviewRemarks, setReviewRemarks] = useState("");
  const [unlockDurationHours, setUnlockDurationHours] = useState(24);
  const [isBulkReview, setIsBulkReview] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const fetchRequests = useCallback(() => {
    setLoading(true);
    setError(null);

    const params: any = { page, pageSize };
    if (statusFilter !== "ALL") params.status = statusFilter;
    if (searchQuery.trim()) params.search = searchQuery.trim();

    ApiService.getAllLcsAccessRequests(params)
      .then((res) => {
        const data = res.data;
        if (data.status === 1) {
          setRequests(data.data || []);
          setTotal(data.pagination?.total || 0);
          setTotalPages(data.pagination?.totalPages || 0);
          if (data.statusCounts) setStatusCounts(data.statusCounts);
        } else {
          setError(data.message || "Failed to load requests");
        }
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to fetch access requests.");
        setLoading(false);
      });
  }, [page, pageSize, statusFilter, searchQuery]);

  useEffect(() => {
    fetchRequests();
  }, [fetchRequests]);

  useEffect(() => {
    setPage(1);
    setSelectedIds(new Set());
  }, [statusFilter, searchQuery]);

  const formatDate = (dateStr: string | null): string => {
    if (!dateStr) return "N/A";
    try {
      return new Date(dateStr).toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return dateStr;
    }
  };

  const getRelativeTime = (dateStr: string): string => {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHours = Math.floor(diffMin / 60);
    if (diffHours < 24) return `${diffHours}h ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays}d ago`;
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const pendingIds = requests.filter((r) => r.Status === "PENDING").map((r) => r.Id);
    if (pendingIds.every((id) => selectedIds.has(id))) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(pendingIds));
    }
  };

  const openReviewModal = (request: AccessRequest | null, action: "APPROVED" | "REJECTED", bulk = false) => {
    setReviewingRequest(request);
    setReviewAction(action);
    setReviewRemarks("");
    setUnlockDurationHours(24);
    setIsBulkReview(bulk);
    setShowReviewModal(true);
  };

  const submitReview = () => {
    if (!reviewAction) return;
    const currentUser = AuthenticationService.currentUserValue;
    setSubmitting(true);

    if (isBulkReview) {
      ApiService.bulkReviewLcsAccessRequests({
        requestIds: Array.from(selectedIds),
        action: reviewAction,
        reviewedByUserId: currentUser?.userId,
        remarks: reviewRemarks || undefined,
        unlockDurationHours: reviewAction === "APPROVED" ? unlockDurationHours : undefined,
      })
        .then((res) => {
          if (res.data.status === 1) {
            setShowReviewModal(false);
            setSelectedIds(new Set());
            fetchRequests();
          } else {
            alert(res.data.message || "Failed to process bulk review.");
          }
        })
        .catch(() => alert("Failed to process bulk review."))
        .finally(() => setSubmitting(false));
    } else if (reviewingRequest) {
      ApiService.reviewLcsAccessRequest(reviewingRequest.Id, {
        action: reviewAction,
        reviewedByUserId: currentUser?.userId,
        remarks: reviewRemarks || undefined,
        unlockDurationHours: reviewAction === "APPROVED" ? unlockDurationHours : undefined,
      })
        .then((res) => {
          if (res.data.status === 1) {
            setShowReviewModal(false);
            fetchRequests();
          } else {
            alert(res.data.message || "Failed to review request.");
          }
        })
        .catch(() => alert("Failed to review request."))
        .finally(() => setSubmitting(false));
    }
  };

  const pendingInView = requests.filter((r) => r.Status === "PENDING");
  const hasSelection = selectedIds.size > 0;

  return (
    <div className="lcs-page">
      {/* Header */}
      <div className="lcs-header">
        <div>
          <h2 className="lcs-title">LCS Access Requests</h2>
          <p className="lcs-subtitle">
            Manage faculty requests for temporary access to update locked lesson plan steps.
          </p>
        </div>
      </div>

      {/* Status filter tabs */}
      <div className="lcs-filter-tabs">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab.key}
            className={`lcs-tab ${statusFilter === tab.key ? "active" : ""}`}
            onClick={() => setStatusFilter(tab.key)}
          >
            {tab.label}
            {statusCounts[tab.key] !== undefined && (
              <span className="lcs-tab-count">{statusCounts[tab.key]}</span>
            )}
          </button>
        ))}
      </div>

      {/* Toolbar */}
      <div className="lcs-toolbar">
        <div className="lcs-search-wrapper">
          <input
            type="text"
            className="lcs-search"
            placeholder="Search by faculty, step, or batch name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {hasSelection && (
          <div className="lcs-bulk-bar">
            <span className="lcs-bulk-count">{selectedIds.size} selected</span>
            <button
              className="lcs-bulk-btn lcs-bulk-approve"
              onClick={() => openReviewModal(null, "APPROVED", true)}
            >
              Approve Selected
            </button>
            <button
              className="lcs-bulk-btn lcs-bulk-reject"
              onClick={() => openReviewModal(null, "REJECTED", true)}
            >
              Reject Selected
            </button>
            <button
              className="lcs-bulk-clear"
              onClick={() => setSelectedIds(new Set())}
            >
              Clear
            </button>
          </div>
        )}
      </div>

      {/* Content */}
      {loading && (
        <div className="lcs-loading">Loading requests...</div>
      )}

      {error && (
        <div className="lcs-error">
          {error}
          <button className="lcs-retry-btn" onClick={fetchRequests}>Retry</button>
        </div>
      )}

      {!loading && !error && requests.length === 0 && (
        <div className="lcs-empty">
          <p>No access requests found{statusFilter !== "ALL" ? ` with status "${statusFilter}"` : ""}.</p>
        </div>
      )}

      {!loading && !error && requests.length > 0 && (
        <>
          <div className="lcs-table-container">
            <table className="lcs-table">
              <thead>
                <tr>
                  {pendingInView.length > 0 && (
                    <th className="lcs-th-check">
                      <input
                        type="checkbox"
                        checked={pendingInView.length > 0 && pendingInView.every((r) => selectedIds.has(r.Id))}
                        onChange={toggleSelectAll}
                        title="Select all pending"
                      />
                    </th>
                  )}
                  <th>Faculty</th>
                  <th>Step</th>
                  <th>Batch</th>
                  <th>Status</th>
                  <th>Requested</th>
                  <th>Reason</th>
                  <th>Reviewed</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {requests.map((req) => (
                  <tr key={req.Id} className={selectedIds.has(req.Id) ? "lcs-row-selected" : ""}>
                    {pendingInView.length > 0 && (
                      <td className="lcs-td-check">
                        {req.Status === "PENDING" ? (
                          <input
                            type="checkbox"
                            checked={selectedIds.has(req.Id)}
                            onChange={() => toggleSelect(req.Id)}
                          />
                        ) : (
                          <span />
                        )}
                      </td>
                    )}
                    <td>
                      <div className="lcs-faculty-name">{req.FacultyName || "Unknown"}</div>
                      {req.FacultyEmail && (
                        <div className="lcs-faculty-email">{req.FacultyEmail}</div>
                      )}
                    </td>
                    <td>{req.StepName || `Step #${req.LessonPlanStepId}`}</td>
                    <td>{req.BatchName || `Batch #${req.BatchId}`}</td>
                    <td>
                      <span className={`lcs-status-badge lcs-status-${req.Status.toLowerCase()}`}>
                        {req.Status}
                      </span>
                    </td>
                    <td>
                      <span title={formatDate(req.RequestedAt)}>
                        {getRelativeTime(req.RequestedAt)}
                      </span>
                    </td>
                    <td className="lcs-reason-cell">
                      {req.Reason ? (
                        <span title={req.Reason}>
                          {req.Reason.length > 40 ? req.Reason.substring(0, 40) + "..." : req.Reason}
                        </span>
                      ) : (
                        <span className="lcs-no-reason">-</span>
                      )}
                    </td>
                    <td>
                      {req.Status !== "PENDING" && req.ReviewedAt ? (
                        <div>
                          <div className="lcs-reviewer-name">{req.ReviewerName || "-"}</div>
                          <div className="lcs-reviewer-date">{formatDate(req.ReviewedAt)}</div>
                          {req.ReviewRemarks && (
                            <div className="lcs-reviewer-remarks" title={req.ReviewRemarks}>
                              {req.ReviewRemarks.length > 30 ? req.ReviewRemarks.substring(0, 30) + "..." : req.ReviewRemarks}
                            </div>
                          )}
                        </div>
                      ) : (
                        <span className="lcs-no-reason">-</span>
                      )}
                    </td>
                    <td className="lcs-actions-cell">
                      {req.Status === "PENDING" ? (
                        <div className="lcs-action-btns">
                          <button
                            className="lcs-btn-approve"
                            onClick={() => openReviewModal(req, "APPROVED")}
                          >
                            Approve
                          </button>
                          <button
                            className="lcs-btn-reject"
                            onClick={() => openReviewModal(req, "REJECTED")}
                          >
                            Reject
                          </button>
                        </div>
                      ) : (
                        <span className="lcs-reviewed-label">Reviewed</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="lcs-pagination">
              <span className="lcs-pagination-info">
                Showing {(page - 1) * pageSize + 1}-{Math.min(page * pageSize, total)} of {total}
              </span>
              <div className="lcs-pagination-btns">
                <button
                  className="lcs-page-btn"
                  disabled={page <= 1}
                  onClick={() => setPage(page - 1)}
                >
                  Previous
                </button>
                {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                  let pageNum: number;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (page <= 3) {
                    pageNum = i + 1;
                  } else if (page >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }
                  return (
                    <button
                      key={pageNum}
                      className={`lcs-page-btn ${page === pageNum ? "active" : ""}`}
                      onClick={() => setPage(pageNum)}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                <button
                  className="lcs-page-btn"
                  disabled={page >= totalPages}
                  onClick={() => setPage(page + 1)}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Review Modal */}
      <Modal
        show={showReviewModal}
        onHide={() => setShowReviewModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {reviewAction === "APPROVED" ? "Approve" : "Reject"}{" "}
            {isBulkReview ? `${selectedIds.size} Request(s)` : "Access Request"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {!isBulkReview && reviewingRequest && (
            <div className="lcs-modal-details">
              <div className="lcs-modal-row">
                <span className="lcs-modal-label">Faculty</span>
                <span>{reviewingRequest.FacultyName || "Unknown"}</span>
              </div>
              <div className="lcs-modal-row">
                <span className="lcs-modal-label">Step</span>
                <span>{reviewingRequest.StepName || `Step #${reviewingRequest.LessonPlanStepId}`}</span>
              </div>
              <div className="lcs-modal-row">
                <span className="lcs-modal-label">Reason</span>
                <span>{reviewingRequest.Reason || "No reason provided"}</span>
              </div>
            </div>
          )}

          {isBulkReview && (
            <p style={{ marginBottom: "1rem" }}>
              You are about to <strong>{reviewAction?.toLowerCase()}</strong> {selectedIds.size} selected request(s).
            </p>
          )}

          {reviewAction === "APPROVED" && (
            <div className="form-group" style={{ marginTop: "1rem" }}>
              <label htmlFor="unlockDuration">Unlock Duration (hours)</label>
              <input
                type="number"
                id="unlockDuration"
                className="form-control"
                value={unlockDurationHours}
                onChange={(e) => setUnlockDurationHours(parseInt(e.target.value) || 24)}
                min={1}
                max={168}
              />
              <small className="form-text text-muted">
                Faculty will have this many hours to update the step (default: 24).
              </small>
            </div>
          )}

          <div className="form-group" style={{ marginTop: "1rem" }}>
            <label htmlFor="reviewRemarks">Remarks (optional)</label>
            <textarea
              id="reviewRemarks"
              className="form-control"
              rows={2}
              value={reviewRemarks}
              onChange={(e) => setReviewRemarks(e.target.value)}
              placeholder="Optional remarks..."
            />
          </div>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="btn btn-secondary"
            onClick={() => setShowReviewModal(false)}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            className={`btn ${reviewAction === "APPROVED" ? "btn-success" : "btn-danger"}`}
            onClick={submitReview}
            disabled={submitting}
          >
            {submitting ? "Processing..." : reviewAction === "APPROVED" ? "Approve" : "Reject"}
          </button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default LcsAccessRequests;
